import React, { useCallback, useMemo, useState } from 'react';

import { Spin, message } from 'antd';

import { useFlexibleTranslation } from '../contexts/LanguageContext';

import useCommunicationsDashboard from '../hooks/useCommunicationsDashboard.js';

import { MOCK_PROJECT_NUMBER } from '../services/communications/communicationsMockData.js';

import { groupItemsBySource, getVisibleSources } from '../services/communications/communicationsPresenters.js';

import { ALIGNMENT_STATUS } from '../services/communications/communicationsTypes.js';

import {

  CommunicationsDashboardShell,

  CommunicationsAppStack,

  CommunicationsFilterBar,

  CommunicationsSetupModal,

  ProductivitySidebar,

  CommunicationsRightSidebar,

  CommunicationsPageHeader,

} from './communications-ui';

import {

  Page,

  BodyLayout,

  CenterColumn,

  FilterSection,

  MainScroll,

  LoadingWrap,

  ErrorText,

} from './communications-ui/communicationsPageStyles.js';



export default function CommunicationsDashboard({ selectedProject }) {

  const { t } = useFlexibleTranslation();

  const projectId = selectedProject?.id;

  const projectName = selectedProject?.name || 'Project';



  const {

    connections,

    feedItems,

    productivity,

    issues,

    members,

    loading,

    error,

    filters,

    setFilters,

    filteredItems,

    activeThreadKey,

    setActiveThreadKey,

    activeConflict,

    configureCommunications,

    askAlignmentQuestion,

    recordAlignedDecision,

    alignmentAnswer,

    alignmentLoading,

    setAlignmentAnswer,

  } = useCommunicationsDashboard(projectId, selectedProject?.members);



  const [selectedItem, setSelectedItem] = useState(null);

  const [setupOpen, setSetupOpen] = useState(false);

  const [productivityCollapsed, setProductivityCollapsed] = useState(false);

  const [rightCollapsed, setRightCollapsed] = useState(false);

  const [rightTab, setRightTab] = useState('issues');

  const [activeIssueId, setActiveIssueId] = useState(null);



  const itemsBySource = useMemo(

    () => groupItemsBySource(filteredItems),

    [filteredItems]

  );



  const visibleSources = useMemo(

    () => getVisibleSources(filters),

    [filters]

  );



  const hasConflict =

    activeConflict?.status === ALIGNMENT_STATUS.CONFLICT ||

    activeConflict?.status === ALIGNMENT_STATUS.PARTIAL;



  const handleSelectItem = useCallback((item) => {

    setSelectedItem(item);

    setActiveThreadKey(item.threadKey);

    setRightTab('detail');

    setRightCollapsed(false);

  }, [setActiveThreadKey]);



  const handleSelectIssue = useCallback((issue) => {

    setActiveIssueId(issue.id);

    setFilters((prev) => ({

      ...prev,

      threadKey: issue.threadKey,

      query: '',

    }));

    setActiveThreadKey(issue.threadKey);

    const threadItem = feedItems.find((item) => item.threadKey === issue.threadKey);

    setSelectedItem(threadItem || null);

    setRightTab('detail');

    setRightCollapsed(false);

  }, [feedItems, setActiveThreadKey, setFilters]);



  const handleConfigure = async (config) => {

    await configureCommunications(config);

    message.success('Communications configured (preview mode)');

  };



  const handleAsk = async (question) => {

    await askAlignmentQuestion(question);

  };



  const handleRecordDecision = async (text) => {

    await recordAlignedDecision(text);

    setAlignmentAnswer(null);

    message.success('Aligned decision recorded (preview mode)');

  };



  if (!selectedProject) {

    return (

      <div style={{ padding: 40, fontFamily: 'Poppins, sans-serif' }}>

        <h2>{t('communications.selectProject', 'Select a project')}</h2>

        <p>{t('communications.selectProjectHint', 'Choose a project from the top navigation to view communications.')}</p>

      </div>

    );

  }



  return (

    <CommunicationsDashboardShell

      overlays={

        <CommunicationsSetupModal

          open={setupOpen}

          onClose={() => setSetupOpen(false)}

          projectName={projectName}

          members={members}

          onComplete={handleConfigure}

        />

      }

    >

      <Page>

        <CommunicationsPageHeader

          title={t('communications.title', 'Project Communications')}

          subtitle={`${projectName} · ${MOCK_PROJECT_NUMBER}`}

          action={

            <button

              type="button"

              className="btn-primary communications-configure-btn"

              onClick={() => setSetupOpen(true)}

            >

              Configure

            </button>

          }

        />



        <BodyLayout>

          <ProductivitySidebar

            productivity={productivity}

            collapsed={productivityCollapsed}

            onExpand={() => setProductivityCollapsed(false)}

            onCollapse={() => setProductivityCollapsed(true)}

          />



          <CenterColumn>

            <FilterSection>

              <CommunicationsFilterBar

                filters={filters}

                onFiltersChange={setFilters}

                members={members}

                resultCount={filteredItems.length}

                hasConflict={hasConflict}

              />

            </FilterSection>



            <MainScroll>

              {loading ? (

                <LoadingWrap><Spin size="large" /></LoadingWrap>

              ) : error ? (

                <ErrorText>{error}</ErrorText>

              ) : (

                <CommunicationsAppStack

                  connections={connections}

                  itemsBySource={itemsBySource}

                  visibleSources={visibleSources}

                  searchQuery={filters.query}

                  activeThreadKey={activeThreadKey}

                  onSelectItem={handleSelectItem}

                />

              )}

            </MainScroll>

          </CenterColumn>



          <CommunicationsRightSidebar

            tab={rightTab}

            onTabChange={setRightTab}

            collapsed={rightCollapsed}

            onCollapse={setRightCollapsed}

            issues={issues}

            activeIssueId={activeIssueId}

            onSelectIssue={handleSelectIssue}

            selectedItem={selectedItem}

            allItems={feedItems}

            activeConflict={activeConflict}

            activeThreadKey={activeThreadKey}

            alignmentAnswer={alignmentAnswer}

            alignmentLoading={alignmentLoading}

            onAsk={handleAsk}

            onRecordDecision={handleRecordDecision}

          />

        </BodyLayout>

      </Page>

    </CommunicationsDashboardShell>

  );

}


